import { type FC, useEffect, useState, useRef } from "react";
import { enqueueSnackbar } from "notistack";
import { LoaderCircle } from "lucide-react";
import Lottie from "react-lottie";

import { EventType, cn, trackEvent } from "@/shared/utils/core";
import { storeOnboardingData } from "@/shared/utils/onboarding";
import {
  getObitScrapeResult,
  getObitScrapeStatus,
  startObitScrape,
} from "@/shared/utils/obitScrapeCache";
import { useQueryClient } from "@tanstack/react-query";

import { useOnboardingAuth } from "@/shared/hooks/useOnboardingAuth";
import { supabase } from "@/shared/lib/supabase";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";
import { getOnboardingFlow, secondsSince, toErrorType } from "@/shared/utils/funnelAnalytics";

import paperplaneAnimation from "@/shared/assets/animations/paperplane.json";

export const ScrapeLoading: FC<FlowCustomComponentProps> = ({
  onComplete,
  answers,
  goToScreen,
  setAnswer,
}) => {
  const scrapeAlreadyCached = getObitScrapeStatus() === "done";
  const [isDone, setIsDone] = useState(false);
  const hasCreatedPersonRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const { createPersonRecord } = useOnboardingAuth();
  const queryClient = useQueryClient();

  const obituaryUrl = typeof answers["obit-link"] === "string" ? answers["obit-link"] : null;

  const scrapeStartedAtRef = useRef<number>(Date.now());
  const fromCacheRef = useRef(scrapeAlreadyCached);
  const analyticsFlowRef = useRef(getOnboardingFlow(answers));
  const setAnswerRef = useRef(setAnswer);
  setAnswerRef.current = setAnswer;
  const createPersonRecordRef = useRef(createPersonRecord);
  createPersonRecordRef.current = createPersonRecord;
  const goToScreenRef = useRef(goToScreen);
  goToScreenRef.current = goToScreen;

  useEffect(() => {
    if (isDone) {
      const delay = scrapeAlreadyCached ? 2500 : 300;
      const timer = setTimeout(() => onCompleteRef.current(), delay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isDone, scrapeAlreadyCached]);

  useEffect(() => {
    let isActive = true;

    if (!obituaryUrl) {
      goToScreenRef.current("has-obituary", { "has-obituary": "no" });
      return;
    }

    // Ensure scrape is started (may already be running from OnboardingAccountPage)
    startObitScrape(obituaryUrl);

    let scrapeTracked = false;

    const processScrape = async () => {
      try {
        const result = await getObitScrapeResult();

        if (!isActive) return;

        if (result.status === "error" || !result.data) {
          throw new Error(result.error || "We couldn't read that obituary.");
        }

        const { extractedData } = result.data;

        const fullName = [
          extractedData.firstName,
          extractedData.middleName,
          extractedData.surName,
          extractedData.suffix,
        ]
          .filter(Boolean)
          .join(" ")
          .trim();

        storeOnboardingData({
          personInfo: {
            fullName,
            dateOfBirth: extractedData.dateOfBirth ?? undefined,
            dateOfDeath: extractedData.dateOfDeath ?? undefined,
            funeralHome: extractedData.funeralHome ?? undefined,
            mainImageUrl: extractedData.mainImageUrl ?? undefined,
            personType: "deceased",
            obituaryLink: obituaryUrl,
          },
        });

        setAnswerRef.current("scraped-name", fullName);
        setAnswerRef.current("scraped-middle-name", extractedData.middleName || "");
        setAnswerRef.current("scraped-dob", extractedData.dateOfBirth || "");
        setAnswerRef.current("scraped-dod", extractedData.dateOfDeath || "");
        setAnswerRef.current("scraped-funeral-home", extractedData.funeralHome || "");
        setAnswerRef.current("scraped-image-url", extractedData.mainImageUrl || "");

        const scrapedCity = extractedData.address?.city || "";
        const scrapedState = extractedData.address?.state || "";
        const scrapedResidence = [scrapedCity, scrapedState].filter(Boolean).join(", ");
        setAnswerRef.current("scraped-residence", scrapedResidence);

        const obituaryData = {
          first_name: extractedData.firstName,
          middle_name: extractedData.middleName || null,
          last_name: extractedData.surName,
          nickname: extractedData.nickname || null,
          image: extractedData.mainImageUrl || null,
          family: extractedData.family || {},
          city: scrapedCity || null,
          state: scrapedState || null,
        };
        setAnswerRef.current("obituary-data", JSON.stringify(obituaryData));

        trackEvent(EventType.OBITUARY_SCRAPE_COMPLETED, {
          flow: analyticsFlowRef.current,
          scrape_result: "success",
          duration_seconds: secondsSince(scrapeStartedAtRef.current),
          from_cache: fromCacheRef.current,
          has_name: Boolean(fullName),
          has_dob: Boolean(extractedData.dateOfBirth),
          has_dod: Boolean(extractedData.dateOfDeath),
          has_location: Boolean(scrapedResidence),
          has_photo: Boolean(extractedData.mainImageUrl),
          family_count: Object.keys(extractedData.family || {}).length,
        });
        scrapeTracked = true;

        if (hasCreatedPersonRef.current) return;

        const personRecord = await createPersonRecordRef.current({
          fullName,
          middleName: extractedData.middleName ?? undefined,
          dateOfBirth: extractedData.dateOfBirth ?? undefined,
          dateOfDeath: extractedData.dateOfDeath ?? undefined,
          funeralHome: extractedData.funeralHome ?? undefined,
          mainImageUrl: extractedData.mainImageUrl ?? undefined,
          lastResidence: scrapedResidence || undefined,
          obituaryLink: obituaryUrl,
        });
        hasCreatedPersonRef.current = true;

        if (!isActive) return;

        setAnswerRef.current("decedent-name", "");
        setAnswerRef.current("decedent-nickname", "");
        setAnswerRef.current("decedent-dob", "");
        setAnswerRef.current("decedent-dod", "");

        if (!personRecord) {
          throw new Error("Failed to create person record");
        }

        const { error: obitUpdateError } = await supabase
          .from("estates")
          .update({
            obituary_data: obituaryData,
            middle_name: extractedData.middleName || null,
          })
          .eq("estate_id", personRecord.estate_id);

        if (obitUpdateError) {
          console.error("Failed to save obituary data:", obitUpdateError);
        }

        await queryClient.refetchQueries({ queryKey: ["app-user-data"], exact: false });
        queryClient.removeQueries({ queryKey: ["person-photo-url"] });

        try {
          await queryClient.prefetchQuery({
            queryKey: ["person-photo-url", personRecord.estate_id, null],
            queryFn: async () => {
              const {
                data: { session },
              } = await supabase.auth.getSession();
              if (!session?.access_token) return null;

              const url = new URL(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media/profile-picture/url`,
              );
              url.searchParams.set("estate_id", String(personRecord.estate_id));

              const response = await fetch(url.toString(), {
                method: "GET",
                headers: {
                  apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
                  Authorization: `Bearer ${session.access_token}`,
                },
              });

              if (!response.ok) return null;
              const responseData = await response.json();
              if (!responseData.photoUrl) return null;

              const imageResponse = await fetch(responseData.photoUrl);
              if (!imageResponse.ok) return null;
              const blob = await imageResponse.blob();
              return URL.createObjectURL(blob);
            },
            staleTime: 30 * 60 * 1000,
          });
        } catch (prefetchErr) {
          console.error("Photo prefetch failed:", prefetchErr);
        }

        setIsDone(true);
      } catch (err) {
        if (!isActive) return;

        const fallback = "We couldn't read that obituary. Enter the details below instead.";
        const rawMessage = err instanceof Error ? err.message : "";
        const isFriendly = rawMessage.startsWith("We couldn't");
        const message = isFriendly ? rawMessage : fallback;

        if (!scrapeTracked) {
          const errorType = toErrorType(err);
          trackEvent(EventType.OBITUARY_SCRAPE_COMPLETED, {
            flow: analyticsFlowRef.current,
            scrape_result: errorType === "timeout" ? "timeout" : "failure",
            duration_seconds: secondsSince(scrapeStartedAtRef.current),
            from_cache: fromCacheRef.current,
            error_type: errorType,
          });
        }
        sentryUtils.captureError(err, rawMessage || fallback, undefined, "warning");

        enqueueSnackbar(message, { variant: "error" });
        goToScreenRef.current("has-obituary", { "has-obituary": "no" });
      }
    };

    processScrape();

    return () => {
      isActive = false;
    };
  }, [obituaryUrl]);

  return <LottieAnimation />;
};

const getLoadingMessages = () => ["Reading the obituary", "Gathering details", "Almost there"];

const MESSAGE_INTERVAL = 3000;

const LottieAnimation: FC = () => {
  const messages = getLoadingMessages();
  const [messageIndex, setMessageIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length);
        setVisible(true);
      }, 300);
    }, MESSAGE_INTERVAL);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="h-80 w-80">
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData: paperplaneAnimation,
            rendererSettings: { preserveAspectRatio: "xMidYMid slice" },
          }}
          isClickToPauseDisabled
        />
      </div>
      <div
        className={cn(
          "flex items-center justify-center gap-3 transition-all duration-300",
          visible ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
        )}
      >
        <LoaderCircle className="h-6 w-6 shrink-0 animate-spin text-secondary" />
        <p className="text-base">{messages[messageIndex]}</p>
      </div>
    </div>
  );
};
